import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type ColumnConfig = {
  id: string;
  label: string;
  visible: boolean;
};

export type ColumnPinSide = 'left' | 'right' | null;

type AvailabilityColumnStore = {
  columns: ColumnConfig[];
  pinnedLeft: string[];
  pinnedRight: string[];
  setColumns: (columns: ColumnConfig[]) => void;
  toggleColumnVisibility: (columnId: string) => void;
  showAllColumns: () => void;
  hideAllColumns: () => void;
  reorderColumns: (fromIndex: number, toIndex: number) => void;
  pinColumn: (columnId: string, side: ColumnPinSide) => void;
  getPinState: (columnId: string) => ColumnPinSide;
  resetColumns: () => void;
  isColumnsDefault: () => boolean;
};

const defaultColumns: ColumnConfig[] = [
  { id: 'rowNumber', label: '', visible: true },
  { id: 'symbol_id', label: 'Symbol ID', visible: true },
  { id: 'sedol', label: 'SEDOL', visible: true },
  { id: 'isin', label: 'ISIN', visible: true },
  { id: 'quantity', label: 'Quantity', visible: true },
  { id: 'benchmark_fee', label: 'Benchmark Fee', visible: true },
  { id: 'ca', label: 'CA', visible: true },
  { id: 'transfers', label: 'Transfers', visible: true },
  { id: 'counterparties', label: 'Counterparties', visible: true },
];

const DEFAULT_PINNED_LEFT = ['rowNumber'];
const DEFAULT_PINNED_RIGHT: string[] = [];

export const useAvailabilityColumnStore = create<AvailabilityColumnStore>()(
  devtools(
  persist(
    (set, get) => ({
      columns: defaultColumns,
      pinnedLeft: DEFAULT_PINNED_LEFT,
      pinnedRight: DEFAULT_PINNED_RIGHT,
      setColumns: (columns: ColumnConfig[]) => set({ columns }),
      toggleColumnVisibility: (columnId: string) =>
        set((state: AvailabilityColumnStore) => {
          const col = state.columns.find((c) => c.id === columnId);
          const becomingHidden = col?.visible === true;
          return {
            columns: state.columns.map((c: ColumnConfig) =>
              c.id === columnId ? { ...c, visible: !c.visible } : c
            ),
            pinnedLeft: becomingHidden
              ? state.pinnedLeft.filter((id) => id !== columnId)
              : state.pinnedLeft,
            pinnedRight: becomingHidden
              ? state.pinnedRight.filter((id) => id !== columnId)
              : state.pinnedRight,
          };
        }),
      showAllColumns: () =>
        set((state: AvailabilityColumnStore) => ({
          columns: state.columns.map((c) => ({ ...c, visible: true })),
        })),
      hideAllColumns: () =>
        set((state: AvailabilityColumnStore) => ({
          columns: state.columns.map((c) => ({ ...c, visible: false })),
          pinnedLeft: state.pinnedLeft.filter((id) => id === 'rowNumber'),
          pinnedRight: [],
        })),
      reorderColumns: (fromIndex: number, toIndex: number) =>
        set((state: AvailabilityColumnStore) => {
          const newColumns = [...state.columns];
          const [movedColumn] = newColumns.splice(fromIndex, 1);
          newColumns.splice(toIndex, 0, movedColumn);
          return { columns: newColumns };
        }),
      pinColumn: (columnId: string, side: ColumnPinSide) =>
        set((state: AvailabilityColumnStore) => {
          if (columnId === 'rowNumber') return state;
          const newLeft = state.pinnedLeft.filter((id) => id !== columnId);
          const newRight = state.pinnedRight.filter((id) => id !== columnId);
          if (side === 'left') newLeft.push(columnId);
          if (side === 'right') newRight.push(columnId);
          return { pinnedLeft: newLeft, pinnedRight: newRight };
        }),
      getPinState: (columnId: string): ColumnPinSide => {
        const state = get();
        if (state.pinnedLeft.includes(columnId)) return 'left';
        if (state.pinnedRight.includes(columnId)) return 'right';
        return null;
      },
      resetColumns: () =>
        set({
          columns: defaultColumns,
          pinnedLeft: DEFAULT_PINNED_LEFT,
          pinnedRight: DEFAULT_PINNED_RIGHT,
        }),
      isColumnsDefault: (): boolean => {
        const state: AvailabilityColumnStore = get();
        if (state.columns.length !== defaultColumns.length) return false;
        const leftMatch = state.pinnedLeft.length === DEFAULT_PINNED_LEFT.length &&
          state.pinnedLeft.every((id, i) => id === DEFAULT_PINNED_LEFT[i]);
        const rightMatch = state.pinnedRight.length === DEFAULT_PINNED_RIGHT.length &&
          state.pinnedRight.every((id, i) => id === DEFAULT_PINNED_RIGHT[i]);
        if (!leftMatch || !rightMatch) return false;
        return state.columns.every(
          (col: ColumnConfig, idx: number) =>
            col.id === defaultColumns[idx].id && col.visible === defaultColumns[idx].visible
        );
      },
    }),
    {
      name: 'availability-columns-storage',
      version: 1,
      merge: (persistedState: unknown, currentState: AvailabilityColumnStore): AvailabilityColumnStore => {
        const persisted =
          typeof persistedState === 'object' && persistedState !== null
            ? (persistedState as Partial<AvailabilityColumnStore>)
            : undefined;
        if (!persisted?.columns?.length) return currentState;
        const persistedById = new Map(persisted.columns.map((c: ColumnConfig) => [c.id, c]));
        const defaultById = new Map(defaultColumns.map((c: ColumnConfig) => [c.id, c]));
        const mergedColumns: ColumnConfig[] = persisted.columns
          .filter((pc: ColumnConfig) => defaultById.has(pc.id))
          .map((pc: ColumnConfig) => ({ ...defaultById.get(pc.id)!, visible: pc.visible }));
        for (const dc of defaultColumns) {
          if (!persistedById.has(dc.id)) {
            mergedColumns.push(dc);
          }
        }
        return {
          ...currentState,
          ...persisted,
          columns: mergedColumns,
          pinnedLeft: Array.isArray(persisted.pinnedLeft) ? persisted.pinnedLeft : DEFAULT_PINNED_LEFT,
          pinnedRight: Array.isArray(persisted.pinnedRight) ? persisted.pinnedRight : DEFAULT_PINNED_RIGHT,
        };
      },
    }
  ),
  { name: 'availability-column-store', enabled: import.meta.env.DEV }
  )
);
